import React from 'react';
import { hasAdminAccess } from '../../utils/user';
import { isEqual } from 'lodash';
import { Icon } from '@cb/apricot-react';
import { localStateReducer, scrollTo, setCheckedProp, sortItems } from '../../utils/common';
import { ResizeContext } from '../../context/ResizeContext';
import { useStateValue } from '../../context/AppContext';

import AmountCheckedDisplay from '../ui/AmountCheckedDisplay';
import Pager from '../ui/pagination/PagerWrapper';
import PageSize from '../ui/pagination/PageSizeWrapper';
import SimpleList from '../ui/list/SimpleList';
import Table from '../ui/table/Table';
import TableControls from '../ui/table/TableControls';

import './Roster.scss';

interface RosterProps<T> {
  // eslint-disable-next-line
  bulkAction: any;
  caption: string;
  // eslint-disable-next-line
  checkboxAllRef: any;
  checkedItems: T[];
  columnSelector: () => JSX.Element;
  emptyMessage: string;
  // eslint-disable-next-line
  goToPage: any;
  groupName: string;
  handleCheckItems: () => void;
  headers: { title: string }[];
  id: string;
  items: T[];
  loading: boolean;
  onPageSizeChange: (num: number) => void;
  pageSize: number;
  renderItem: (item: T, options: { mobile: boolean }) => void;
  showChecked: boolean;
  showSelected: boolean;
  sortFields: { name: string }[];
  sortStrategy: 'natural' | 'numeric' | 'alphabetic';
  stickyHeaders: boolean;
  tableClassName?: string;
  // eslint-disable-next-line
  tableControlElements: any[];
  // eslint-disable-next-line
  tableControlForm: any;
  tableStyleShadow: boolean;
  tableStyleLight: boolean;
  tableStyleReverseStripes: boolean;
  tableStyleBorderless: boolean;
  forceDesktopMode: boolean;
}

function Roster<T>({
  bulkAction = null,
  caption = '',
  checkboxAllRef = {},
  checkedItems = [],
  columnSelector,
  emptyMessage = '',
  goToPage = undefined,
  groupName = '',
  handleCheckItems = () => {},
  headers = [],
  id = '',
  items = [],
  loading = false,
  onPageSizeChange,
  pageSize = 30,
  renderItem = () => {},
  showChecked = true,
  showSelected = false,
  sortFields = [],
  sortStrategy = 'natural',
  stickyHeaders = false,
  tableClassName = 'p-0 mb-4',
  tableControlElements = [],
  tableControlForm = null,
  tableStyleShadow = true,
  tableStyleLight = false,
  tableStyleReverseStripes = false,
  tableStyleBorderless = false,
  forceDesktopMode = false,
}: RosterProps<T>) {
  // TODO: Refactor to use renderHeader from RosterHeader.tsx
  // eslint-disable-next-line
  function renderHeader(header: { title: string; sortField: any[] }, index: number) {
    const key = `header_${header.title}_${index}`;
    const headerSortField =
      header.sortField && header.sortField.length > 0 ? header.sortField[header.sortField.length - 1] : '';
    return (
      <th scope='col' aria-sort={headerSortOrder(headerSortField)} className={stickyHeaders ? 'sticky' : ''} key={key}>
        {header.sortField ? (
          <a
            role='button'
            className={tableStyleLight ? 'text--color-black1' : 'text--color-white'}
            data-automation={`roster-${header.title.split(' ').join('-').toLowerCase()}`}
            href='#0'
            onClick={handleHeaderOnClick(header.sortField)}
            onKeyDown={(e) => {
              //Since role is button, make sure to also support space key for accessibility
              if (e.key === ' ') {
                e.preventDefault();
                // eslint-disable-next-line
                handleHeaderOnClick(header.sortField)(e as any);
              }
            }}
          >
            {header.title}
            {generateIcon(header.sortField)}
          </a>
        ) : (
          <span data-automation={`roster-${header.title.split(' ').join('-').toLowerCase()}`}>{header.title}</span>
        )}
      </th>
    );
  }

  // TODO: Refactor to use headerSortOrder and generateIcon from RosterUtils.tsx
  function headerSortOrder(field?: string) {
    // See if this field is already being sorted.
    const matchingField = localState.sortFields.find((item: { name: string }) => item.name === field);

    // Invert the sort criteria if necessary.
    if (!matchingField || !matchingField.order) {
      return 'none';
    } else {
      return matchingField.order === 'asc' ? 'ascending' : 'descending';
    }
  }

  function generateIcon(field: string[]) {
    // We're using arrays now, so just use the last one in the list.
    const sortOrder = headerSortOrder(field[field.length - 1]);

    if (sortOrder === 'none') {
      return null;
    } else {
      return <Icon name={sortOrder === 'ascending' ? 'sort-asc' : 'sort-desc'} decorative />;
    }
  }

  function handleHeaderOnClick(fields: string[]) {
    return function (e: Event) {
      e && e.preventDefault && e.preventDefault();

      let matchingIndex = -1;
      const newSortFields = JSON.parse(JSON.stringify(localState.sortFields));
      let oldSortObj = {};
      let order = 'asc';

      // We might be passing an array of sortable fields per column header, so go through them.
      fields.forEach((field: string) => {
        // Search the current list of sorted fields, and adjust accordingly.
        matchingIndex = newSortFields.findIndex((sortField: { name: string }) => sortField.name === field);

        if (matchingIndex > -1) {
          // Remove the old obj, while returning the object that was removed.
          oldSortObj = newSortFields.splice(matchingIndex, 1)[0];

          // Change the order as per rotation -> asc -> desc -> default.
          if (oldSortObj.order === 'asc') {
            order = 'desc';
          } else if (oldSortObj.order === 'desc') {
            order = 'default';
          }
        }

        // Add it back in to the beginning of the order array with the new order.
        if (order !== 'default') {
          newSortFields.unshift({
            name: field,
            order,
          });
        }
      });

      setLocalState({
        paginationPage: 0,
        sortFields: newSortFields,
      });
    };
  }

  function mobileComponent(items) {
    return <SimpleList id={id} items={items} renderItem={(item) => renderItem(item, { mobile: true })} />;
  }

  function desktopComponent() {
    function handleCheckAll(toCheck) {
      return function (e) {
        const checked = e.target.checked;
        const checkItems = [];

        // Loop through the checked items and add as needed.
        toCheck.forEach((item) => {
          checkItems.push(item.id);
        });

        handleCheckItems(checkItems, checked);
      };
    }

    return (
      <Table
        checkboxAllRef={checkboxAllRef}
        handleCheckAll={handleCheckAll}
        headers={headers}
        id={id}
        items={displayItems}
        options={{ caption }}
        renderHeader={renderHeader}
        renderItem={renderItem}
        stickyHeaders={stickyHeaders}
        shadow={tableStyleShadow}
        tableStyleLight={tableStyleLight}
        tableClassName={tableClassName}
        tableStyleReverseStripes={tableStyleReverseStripes}
        tableStyleBorderless={tableStyleBorderless}
      />
    );
  }

  // Global App state.
  const { user } = useStateValue();

  // Global App state.
  const windowSize = React.useContext(ResizeContext);

  // Local state.
  const [localState, setLocalState] = React.useReducer(localStateReducer, {
    initialItems: items,
    paginationPage: 0,
    sortFields,
    sortStrategy,
    currentPageSize: pageSize || 30,
  });

  const handleChangePageSize = React.useCallback(
    // eslint-disable-next-line
    (num: number) => {
      return function (e: Event) {
        e && e.preventDefault && e.preventDefault();
        const currentPageSize = num;

        // Un-check the "check all" checkbox if they change pages.
        setCheckedProp(checkboxAllRef, false);

        const maxPages = Math.ceil(items.length / currentPageSize);
        let paginationPage = localState.paginationPage;

        if (paginationPage < 0) {
          paginationPage = 0;
        } else if (paginationPage >= maxPages) {
          paginationPage = maxPages - 1;
        }

        //updating local state for all students Page Size preference
        onPageSizeChange && onPageSizeChange(currentPageSize);
        setLocalState({ currentPageSize, paginationPage });
      };
    },
    // eslint-disable-next-line
    [items.length, localState.currentPageSize, localState.paginationPage]
  );

  const handleChangePage = React.useCallback(
    // eslint-disable-next-line
    (num: number) => {
      return function (e) {
        e && e.preventDefault && e.preventDefault();

        // Un-check the "check all" checkbox if they change pages.
        setCheckedProp(checkboxAllRef, false);

        const maxPages = Math.ceil(items.length / localState.currentPageSize);

        if (typeof num !== 'number') {
          num = 0;
        } else if (num < 0) {
          num = 0;
        } else if (num >= maxPages) {
          num = maxPages - 1;
        }

        if (localState.paginationPage !== num) {
          setLocalState({
            paginationPage: num,
          });
        }
        // Scroll to the top of the Roster.
        scrollTo('top-of-table');
      };
    },
    // eslint-disable-next-line
    [checkboxAllRef, items.length, localState.paginationPage, localState.currentPageSize]
  );

  const sortedItems = localState.sortFields ? sortItems(items, localState.sortFields, localState.sortStrategy) : items;

  const startIndex = localState.paginationPage * localState.currentPageSize;
  const endIndex = localState.paginationPage * localState.currentPageSize + localState.currentPageSize;
  const displayItems = sortedItems.slice(startIndex, endIndex);

  const showPageOpts: boolean = items.length > 30;
  //determine if the function has been passed to props and exists
  const showDynamicColumnsButton: boolean = typeof columnSelector === 'function';

  React.useEffect(() => {
    // Update the page to 1 if we get an updated or filtered list.
    if (!isEqual(localState.initialItems, items)) {
      setLocalState({
        initialItems: items,
        paginationPage: 0,
      });
    }
  }, [localState.initialItems, items, localState.paginationPage]);

  // Set the "Check all" box to checked if they manually checked all of their display items.
  const displayedRegNos = displayItems.map((m) => m.candRegNo);
  const allDisplayedItemsChecked = displayedRegNos.every((v) => checkedItems.includes(v));

  if (allDisplayedItemsChecked) {
    setCheckedProp(checkboxAllRef, true);
  }

  React.useEffect(() => {
    if (typeof goToPage === 'function' && sortedItems.length > localState.currentPageSize) {
      const gotoPage = goToPage(sortedItems, localState.currentPageSize);
      if (gotoPage) {
        handleChangePage(gotoPage)();
      }
    }
  }, [goToPage, sortedItems, handleChangePage, localState.currentPageSize]);

  const pageSizeOptions = (itemsLength: number) => {
    if (itemsLength <= 30) {
      return [];
    } else if (itemsLength > 30 && itemsLength <= 60) {
      return [30, 60];
    } else if (itemsLength > 60 && itemsLength <= 90) {
      return [30, 60, 90];
    } else {
      return [30, 60, 90, 120];
    }
  };
  return (
    <React.Fragment>
      {<TableControls tableControlElements={tableControlElements} tableControlForm={tableControlForm} />}
      {!!items.length && !!groupName && showChecked && (
        <div
          className={
            showPageOpts
              ? 'row mt-0 pt-4 display-results-sticky'
              : windowSize.mobile || !showDynamicColumnsButton
                ? 'row my-4'
                : 'row mt-4'
          }
        >
          {
            <div className='col-sm-6'>
              <AmountCheckedDisplay
                amountSelected={checkedItems.length}
                endDisplayIndex={startIndex + displayItems.length}
                itemsPerPage={localState.currentPageSize}
                showSelected={hasAdminAccess(user.rid, user.role) && showSelected}
                startDisplayIndex={startIndex + 1}
                tableName={caption}
                totalCount={items.length}
                loading={loading}
              />
            </div>
          }
          {bulkAction && <div className='col-sm-6'>{bulkAction}</div>}
        </div>
      )}

      {emptyMessage ? (
        <div className='row' style={{ marginTop: '1rem' }} aria-live='polite'>
          <div className='col-xs-12' data-automation='no-rooms-message'>
            {emptyMessage}
          </div>
        </div>
      ) : !displayItems.length ? (
        <div className='row' style={{ marginTop: '1rem' }} aria-live='polite'>
          <span style={{ marginLeft: '1rem' }}>0 results. Try again.</span>
        </div>
      ) : (
        <React.Fragment>
          {showPageOpts ? (
            <div className='cb-pagination-size row mt-0 py-2 page-opts-sticky'>
              <div className='col-12 col-sm-6'>
                <div className='page-size-align display--flex_desktop_align-items--center'>
                  {!windowSize.mobile && showDynamicColumnsButton && columnSelector()}
                  <PageSize
                    maxRecords={items.length}
                    pagesizeId='pageSizeOption'
                    buttonId='pagerBtnOption'
                    current={localState.currentPageSize}
                    ariaLabel='Page Size Component'
                    pageSizeOptions={pageSizeOptions(items.length)}
                    onSizeChange={(num: number) => handleChangePageSize(num)()}
                    recordCountLabel={''}
                  />
                </div>
              </div>

              <div className='col-12 col-sm-6 mt-4 mt-sm-0'>
                <Pager
                  ariaLabel='pagination'
                  buttonId='studentRoster-pagination-page'
                  className='d-flex w-100 pager-align'
                  current={localState.paginationPage + 1}
                  delta={4}
                  max={Math.ceil(items.length / localState.currentPageSize)}
                  onPageChange={(num: number) => handleChangePage(num - 1)()}
                  pagerId='studentRoster-Pagination'
                />
              </div>
            </div>
          ) : (
            !windowSize.mobile &&
            showDynamicColumnsButton && (
              <div className='cb-pagination-size row mt-0 py-2'>
                <div className='col-12 col-sm-6'>
                  <div className='page-size-align'>{columnSelector()}</div>
                </div>
              </div>
            )
          )}
          <div className={showPageOpts ? 'table--mod-sticky' : ''}>
            {forceDesktopMode
              ? desktopComponent(displayItems)
              : windowSize.mobile
                ? mobileComponent(displayItems)
                : desktopComponent(displayItems)}
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default Roster;
